import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Tooltip,
} from 'recharts';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { PenLine, Lightbulb, FileText, MessageSquareQuote, Plus, Sparkles, BookOpen } from 'lucide-react';

/**
 * Mains-specific analytics surfaces from
 * GET /api/student-strategy/mains-analytics
 *
 * Three components:
 *   - MainsWritingPerformance     -> per-paper avg / word-count / grade chips
 *   - MainsRecurringImprovements  -> ranked feedback themes feed
 *   - MainsRubricRadar            -> 7-dimension radar per paper (Visual tab)
 *
 * Visible when examType is 'mains' or 'all'.
 */

const WC_BADGE = {
  too_brief: 'bg-amber-50 text-amber-700 border-amber-200',
  on_target: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  too_verbose: 'bg-orange-50 text-orange-700 border-orange-200',
};
const WC_LABEL = {
  too_brief: 'Too brief',
  on_target: 'On target',
  too_verbose: 'Too long',
};

const GRADE_COLOR = {
  A: 'text-emerald-600',
  B: 'text-blue-600',
  C: 'text-amber-600',
  D: 'text-orange-600',
  E: 'text-red-600',
};

const pctColor = (p) => {
  if (p >= 75) return 'text-emerald-600';
  if (p >= 60) return 'text-blue-600';
  if (p >= 45) return 'text-amber-600';
  return 'text-red-600';
};

/** Canonical paper list — we want all 5 to surface even with 0 answers,
 *  so the user can see exactly which papers they haven't written yet.
 *  All CTAs route to /mains (the hub) where the user picks the paper. */
const ALL_MAINS_PAPERS = [
  { key: 'gs1', name: 'GS Paper I' },
  { key: 'gs2', name: 'GS Paper II' },
  { key: 'gs3', name: 'GS Paper III' },
  { key: 'gs4', name: 'GS Paper IV (Ethics)' },
  { key: 'essay', name: 'Essay' },
];

const EmptyPaperCard = ({ paper, onWrite }) => (  <div
    className="border border-dashed border-slate-300 rounded-lg p-3 bg-slate-50/50 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors flex flex-col"
    data-testid={`mains-paper-${paper.key}-empty`}
  >
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-medium text-slate-500">{paper.name}</span>
      <span className="text-base font-bold text-slate-300">—</span>
    </div>
    <p className="text-[11px] text-slate-400 flex-1">No answers yet</p>
    <Button
      variant="outline"
      size="sm"
      className="mt-2 h-7 text-[11px] text-emerald-700 border-emerald-200 hover:bg-emerald-100"
      onClick={onWrite}
      data-testid={`mains-write-cta-${paper.key}`}
    >
      <Plus className="w-3 h-3 mr-1" />
      Write first answer
    </Button>
  </div>
);

/**
 * Mains Engagement card — surfaces "model answers generated" count from
 * /api/student-strategy/mains-analytics > engagement block.
 *
 * Generating a model answer (clicking "Generate GS-I answer") is passive
 * study, like reading a textbook example. We track it as ENGAGEMENT
 * (feeds streak/consistency) but NOT as mastery. This card makes the
 * effort visible so users get credit without polluting their scores.
 */
export const MainsEngagement = ({ data, examType }) => {
  if (!data || (examType !== 'mains' && examType !== 'all')) return null;

  const engagement = data.engagement;
  if (!engagement) return null;

  const generated = engagement.model_answers_generated || 0;
  const evaluated = data.total_answers || 0;
  const ratio = engagement.eval_ratio || 0;
  const byPaper = engagement.generated_by_paper || {};

  if (generated === 0 && evaluated === 0) return null;

  return (
    <Card className="border-0 shadow-md" data-testid="mains-engagement-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-500" />
            <h3 className="font-semibold text-slate-800">Practice &amp; Engagement</h3>
          </div>
          <Badge variant="outline" className="text-[10px] bg-indigo-50 border-indigo-200 text-indigo-700">
            Engagement signal · doesn&apos;t affect mastery
          </Badge>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-amber-50 rounded-lg p-3" data-testid="metric-model-answers">
            <div className="flex items-center gap-1.5 mb-1">
              <BookOpen className="w-3.5 h-3.5 text-amber-600" />
              <span className="text-[11px] uppercase tracking-wide text-amber-700 font-medium">
                Model answers generated
              </span>
            </div>
            <div className="text-2xl font-bold text-amber-800">{generated}</div>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3" data-testid="metric-evaluated">
            <div className="flex items-center gap-1.5 mb-1">
              <PenLine className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-[11px] uppercase tracking-wide text-emerald-700 font-medium">
                Your answers evaluated
              </span>
            </div>
            <div className="text-2xl font-bold text-emerald-800">{evaluated}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3" data-testid="metric-eval-ratio">
            <div className="flex items-center gap-1.5 mb-1">
              <Lightbulb className="w-3.5 h-3.5 text-slate-600" />
              <span className="text-[11px] uppercase tracking-wide text-slate-700 font-medium">
                Eval ratio
              </span>
            </div>
            <div className="text-2xl font-bold text-slate-800">{ratio}%</div>
          </div>
        </div>

        {Object.keys(byPaper).length > 0 && (
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-1.5">
              Generated by paper
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(byPaper).map(([paper, count]) => (
                <Badge
                  key={paper}
                  variant="outline"
                  className="text-xs bg-white border-slate-200 text-slate-700"
                >
                  {paper.toUpperCase()} · {count}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {generated > 0 && evaluated === 0 && (
          <div className="mt-3 bg-amber-50/70 border border-amber-200 rounded-md p-2.5 text-[11px] text-amber-900">
            You&apos;ve generated {generated} model {generated === 1 ? 'answer' : 'answers'} but
            haven&apos;t submitted any for evaluation yet. Submit your own answer for grading to
            unlock mastery scores.
          </div>
        )}
        {generated > 0 && evaluated > 0 && ratio < 30 && (
          <div className="mt-3 bg-blue-50/70 border border-blue-200 rounded-md p-2.5 text-[11px] text-blue-900">
            Tip: only {ratio}% of your activity has been evaluated. Try submitting more own-answers
            for grading to sharpen your mastery scores.
          </div>
        )}
      </CardContent>
    </Card>
  );
};


export const MainsWritingPerformance = ({ data, examType }) => {  const navigate = useNavigate();
  if (!data || (examType !== 'mains' && examType !== 'all')) return null;

  const papersWithData = data.has_data ? data.papers || [] : [];
  const writtenKeys = new Set(papersWithData.map((p) => p.paper_key));
  const missingPapers = ALL_MAINS_PAPERS.filter((p) => !writtenKeys.has(p.key));

  return (
    <Card className="border-0 shadow-md" data-testid="mains-writing-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <PenLine className="w-4 h-4 text-emerald-500" />
            <h3 className="font-semibold text-slate-800">Mains Writing Performance</h3>
          </div>
          <Badge variant="outline" className="text-[10px]">
            {data.total_answers || 0} answers
            {data.total_answers > 0 && ` · ${data.overall_avg_percentage}% avg`}
          </Badge>
        </div>

        {!data.has_data && (
          <p className="text-sm text-slate-500 mb-3">
            You haven&apos;t written any Mains answers yet. Pick a paper to start.
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Real papers with data */}
          {papersWithData.map((p) => (
            <div
              key={p.paper_key}
              className="border border-slate-200 rounded-lg p-3 hover:border-emerald-300 transition-colors"
              data-testid={`mains-paper-${p.paper_key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">{p.paper}</span>
                <span className={`text-lg font-bold ${pctColor(p.avg_percentage)}`}>
                  {p.avg_percentage}%
                </span>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-2">
                <FileText className="w-3 h-3" />
                {p.answers_count} answers · {p.avg_word_count || '—'} words
                {p.word_count_status && (
                  <Badge variant="outline" className={`text-[9px] px-1 py-0 ml-1 ${WC_BADGE[p.word_count_status]}`}>
                    {WC_LABEL[p.word_count_status]}
                  </Badge>
                )}
              </div>
              {/* Grade distribution mini-strip */}
              <div className="flex items-center gap-2">
                {['A', 'B', 'C', 'D', 'E'].map((g) => {
                  const cnt = p.grade_distribution?.[g] || 0;
                  if (!cnt) return null;
                  return (
                    <div key={g} className="flex items-baseline gap-0.5">
                      <span className={`text-xs font-bold ${GRADE_COLOR[g]}`}>{g}</span>
                      <span className="text-[10px] text-slate-400">×{cnt}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Missing papers — render as "write first answer" CTAs */}
          {missingPapers.map((p) => (
            <EmptyPaperCard
              key={p.key}
              paper={p}
              onWrite={() => navigate('/mains')}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const MainsRecurringImprovements = ({ data, examType }) => {
  if (!data || (examType !== 'mains' && examType !== 'all')) return null;
  if (!data.recurring_improvements || data.recurring_improvements.length === 0) return null;
  return (
    <Card className="border-0 shadow-md" data-testid="mains-improvements-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb className="w-4 h-4 text-amber-500" />
          <h3 className="font-semibold text-slate-800">Recurring Feedback Themes</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Across {data.total_answers} evaluations, your evaluator most often suggested:
        </p>
        <ul className="space-y-2">
          {data.recurring_improvements.slice(0, 6).map(([text, count], idx) => (
            <li
              key={idx}
              className="flex items-start gap-2 text-sm bg-amber-50/50 border border-amber-100 rounded-lg px-3 py-2"
              data-testid={`improvement-${idx}`}
            >
              <MessageSquareQuote className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
              <span className="text-slate-700 flex-1">{text}</span>
              <Badge variant="outline" className="text-[10px] bg-white border-amber-200 text-amber-700 flex-shrink-0">
                {count}×
              </Badge>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export const MainsRubricRadar = ({ data, examType }) => {
  const navigate = useNavigate();
  if (!data || (examType !== 'mains' && examType !== 'all')) return null;

  // Papers with rubric dimension data available
  const papersWithRubric = (data.papers || []).filter(
    (p) => Array.isArray(p.rubric_breakdown) && p.rubric_breakdown.length > 0,
  );
  const gradedKeys = new Set(papersWithRubric.map((p) => p.paper_key));
  const missingPapers = ALL_MAINS_PAPERS.filter((p) => !gradedKeys.has(p.key));

  // If nothing graded AND not in mains mode, hide entirely
  if (papersWithRubric.length === 0 && examType !== 'mains') return null;

  return (
    <Card className="border-0 shadow-md" data-testid="mains-rubric-radar-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <PenLine className="w-4 h-4 text-emerald-500" />
            <h3 className="font-semibold text-slate-800">Mains Rubric Breakdown</h3>
          </div>
          <Badge variant="outline" className="text-[10px]">7 dimensions</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Graded papers — render real radar */}
          {papersWithRubric.map((p) => {
            const maxScore = Math.max(...p.rubric_breakdown.map((r) => r.avg_score), 1);
            const chartData = p.rubric_breakdown.map((r) => ({
              dimension: r.dimension,
              score: r.avg_score,
              fullMark: Math.ceil(maxScore),
            }));
            return (
              <div
                key={p.paper_key}
                className="border border-slate-200 rounded-lg p-3"
                data-testid={`rubric-radar-${p.paper_key}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">{p.paper}</span>
                  <span className="text-[11px] text-slate-500">{p.answers_count} answers</span>
                </div>
                <div className="w-full h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={chartData} outerRadius="75%">
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 9, fill: '#475569' }} />
                      <PolarRadiusAxis
                        angle={90}
                        domain={[0, Math.ceil(maxScore)]}
                        tick={{ fontSize: 9, fill: '#94a3b8' }}
                      />
                      <Radar
                        name={p.paper}
                        dataKey="score"
                        stroke="#10b981"
                        fill="#10b981"
                        fillOpacity={0.35}
                      />
                      <Tooltip
                        contentStyle={{ fontSize: '12px', borderRadius: '6px' }}
                        formatter={(v) => [v, 'Avg']}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            );
          })}

          {/* Missing papers — empty-state with CTA */}
          {missingPapers.map((p) => (
            <div
              key={p.key}
              className="border border-dashed border-slate-300 rounded-lg p-3 bg-slate-50/50 flex flex-col items-center justify-center text-center min-h-[240px]"
              data-testid={`rubric-radar-${p.key}-empty`}
            >
              <span className="text-sm font-medium text-slate-500 mb-1">{p.name}</span>
              <span className="text-[11px] text-slate-400 mb-3">No graded answers yet</span>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-[11px] text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                onClick={() => navigate('/mains')}
                data-testid={`rubric-write-cta-${p.key}`}
              >
                <Plus className="w-3 h-3 mr-1" />
                Write first answer
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
